import React, { useState } from 'react';
import {
  Tree,
  Card,
  Tag,
  Progress,
  Space,
  Typography,
  Tooltip,
  Button,
  Collapse,
  Badge
} from 'antd';
import {
  AimOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CaretRightOutlined,
  UserOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  ThunderboltOutlined,
  RestOutlined,
  SmileOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;
const { Panel } = Collapse;

// Pillar icons and colors
const PILLAR_CONFIG = {
  'Medication': { icon: <MedicineBoxOutlined />, color: '#1890ff' },
  'Nutrition': { icon: <HeartOutlined />, color: '#52c41a' },
  'Movement': { icon: <ThunderboltOutlined />, color: '#fa8c16' },
  'Recovery': { icon: <RestOutlined />, color: '#722ed1' },
  'Emotion': { icon: <SmileOutlined />, color: '#eb2f96' }
};

const TIER_COLORS = {
  1: '#52c41a',
  2: '#1890ff', 
  3: '#fa8c16',
  4: '#f5222d'
};

const STATUS_COLORS = {
  'Not Started': 'default',
  'In Progress': 'processing',
  'Completed': 'success',
  'On Hold': 'warning',
  'Cancelled': 'error'
};

/**
 * PatientGoalTree - A hierarchical tree view for displaying SMART goals
 * Shows MD goals as parent nodes with patient goals as children
 */
const PatientGoalTree = ({
  goals = [],
  onGoalSelect,
  onPatientGoalSelect,
  selectedGoalId,
  selectedPatientGoalId,
  showActions = false,
  compact = false
}) => {
  const [expandedKeys, setExpandedKeys] = useState([]);

  const getStatusIcon = (status) => {
    const icons = {
      'Not Started': <ClockCircleOutlined />,
      'In Progress': <ClockCircleOutlined />,
      'Completed': <CheckCircleOutlined />,
      'On Hold': <ExclamationCircleOutlined />,
      'Cancelled': <ExclamationCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const isOverdue = (timeline) => {
    return timeline?.targetDate && moment().isAfter(moment(timeline.targetDate));
  };

  const renderPatientGoalTitle = (patientGoal, goalId) => {
    const pillarConfig = PILLAR_CONFIG[patientGoal.relatedPillar] || { icon: <UserOutlined />, color: '#666' };
    const overdue = isOverdue(patientGoal.timeline);
    
    return (
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%',
          padding: '4px 0'
        }}
        onClick={(e) => {
          e.stopPropagation();
          onPatientGoalSelect?.(goalId, patientGoal._id, patientGoal);
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <div style={{ color: pillarConfig.color }}>
            {pillarConfig.icon}
          </div>
          <Text 
            style={{ 
              fontSize: compact ? '12px' : '13px',
              fontWeight: selectedPatientGoalId === patientGoal._id ? 'bold' : 'normal'
            }}
            ellipsis={{ tooltip: patientGoal.smartGoal }}
          >
            {patientGoal.smartGoal || `${patientGoal.relatedPillar} Goal`}
          </Text>
        </div>
        
        <Space size={4}>
          <Tag 
            color={pillarConfig.color} 
            size="small"
            style={{ fontSize: '10px', margin: 0 }}
          >
            {patientGoal.relatedPillar}
          </Tag>
          <Tag 
            color={TIER_COLORS[patientGoal.tier]} 
            size="small"
            style={{ fontSize: '10px', margin: 0 }}
          >
            T{patientGoal.tier}
          </Tag>
          <Tag 
            color={STATUS_COLORS[patientGoal.status]} 
            size="small"
            style={{ fontSize: '10px', margin: 0 }}
          >
            {patientGoal.status}
          </Tag>
          {overdue && (
            <Tag color="error" size="small" style={{ fontSize: '10px', margin: 0 }}>
              Overdue
            </Tag>
          )}
          <Progress
            type="circle"
            size={20}
            percent={patientGoal.completionRate || 0}
            strokeWidth={8}
            format={() => `${patientGoal.completionRate || 0}%`}
            style={{ fontSize: '8px' }}
          />
        </Space>
      </div>
    );
  };

  const renderMDGoalTitle = (goal) => {
    const overdue = goal.patientGoals?.some(pg => isOverdue(pg.timeline));
    
    return (
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%',
          padding: '4px 0'
        }}
        onClick={(e) => {
          e.stopPropagation();
          onGoalSelect?.(goal._id, goal);
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <AimOutlined style={{ color: '#1890ff' }} />
          <div>
            <Text 
              strong 
              style={{ 
                fontSize: compact ? '13px' : '14px',
                color: selectedGoalId === goal._id ? '#1890ff' : 'inherit'
              }}
            >
              {goal.problem?.problemName || 'SMART Goal'}
            </Text>
            {goal.problem?.icdCode && (
              <Text type="secondary" style={{ fontSize: '11px', marginLeft: 4 }}>
                ({goal.problem.icdCode})
              </Text>
            )}
          </div>
        </div>
        
        <Space size={4}>
          <Badge 
            count={goal.patientGoals?.length || 0} 
            size="small"
            style={{ backgroundColor: '#52c41a' }}
          />
          <Tag 
            color={STATUS_COLORS[goal.status]} 
            icon={getStatusIcon(goal.status)}
            size="small"
          >
            {goal.status}
          </Tag>
          {overdue && (
            <Tag color="error" size="small">
              Overdue
            </Tag>
          )}
          <Progress
            type="circle"
            size={24}
            percent={goal.overallCompletionRate || 0}
            strokeWidth={6}
            format={() => `${goal.overallCompletionRate || 0}%`}
          />
        </Space>
      </div>
    );
  };

  const buildTreeData = () => {
    return goals.map(goal => ({
      key: goal._id,
      title: renderMDGoalTitle(goal),
      children: goal.patientGoals?.map(patientGoal => ({
        key: `${goal._id}-${patientGoal._id}`,
        title: renderPatientGoalTitle(patientGoal, goal._id),
        isLeaf: true,
        data: { type: 'patientGoal', goal, patientGoal }
      })) || [],
      data: { type: 'mdGoal', goal }
    }));
  };

  const handleExpand = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue);
  };

  const handleSelect = (selectedKeys, info) => {
    const { node } = info;
    if (node.data?.type === 'mdGoal') {
      onGoalSelect?.(node.data.goal._id, node.data.goal);
    } else if (node.data?.type === 'patientGoal') {
      onPatientGoalSelect?.(node.data.goal._id, node.data.patientGoal._id, node.data.patientGoal);
    }
  };

  if (!goals || goals.length === 0) {
    return (
      <Card size="small">
        <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
          <AimOutlined style={{ fontSize: 48, marginBottom: 16 }} />
          <div>No SMART goals found</div>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      size="small" 
      title={
        <Space>
          <AimOutlined />
          <span>SMART Goals Tree</span>
          <Badge count={goals.length} size="small" />
        </Space>
      }
      className="patient-goal-tree"
    >
      <Tree
        showLine={{ showLeafIcon: false }}
        showIcon={false}
        expandedKeys={expandedKeys}
        onExpand={handleExpand}
        onSelect={handleSelect}
        selectedKeys={selectedGoalId ? [selectedGoalId] : []}
        treeData={buildTreeData()}
        switcherIcon={<CaretRightOutlined />}
        style={{
          backgroundColor: 'transparent'
        }}
      />
      
      <style jsx>{`
        .patient-goal-tree .ant-tree-node-content-wrapper {
          width: calc(100% - 24px);
        }
        
        .patient-goal-tree .ant-tree-title {
          width: 100%;
        }
        
        .patient-goal-tree .ant-tree-node-content-wrapper:hover {
          background-color: #f5f5f5;
        }
        
        .patient-goal-tree .ant-tree-node-selected .ant-tree-node-content-wrapper {
          background-color: #e6f7ff;
        }
      `}</style>
    </Card>
  );
};

export default PatientGoalTree;
